/**
 * Centralized enums and constants for the application
 * This file contains all static strings used throughout the app
 */

import { BizEntityTypeCategory } from "@optima-stride-ai/core";
/**
 * File extensions enum
 */
export enum FileExtensions {
  // Document files
  PDF = '.pdf',
  DOC = '.doc',
  DOCX = '.docx',
  ODT = '.odt',
  TXT = '.txt',
  RTF = '.rtf',

  // Spreadsheet files
  XLS = '.xls',
  XLSX = '.xlsx',
  ODS = '.ods',
  CSV = '.csv',

  // Presentation files
  PPT = '.ppt',
  PPTX = '.pptx',
  ODP = '.odp',

  // Image files
  JPG = '.jpg',
  JPEG = '.jpeg',
  PNG = '.png',
  GIF = '.gif',
  BMP = '.bmp',
  WEBP = '.webp',
  SVG = '.svg',

  // Video files
  MP4 = '.mp4',
  AVI = '.avi',
  MOV = '.mov',
  WMV = '.wmv',
  FLV = '.flv',
  WEBM = '.webm',
  MKV = '.mkv',
  M4V = '.m4v',
  THREE_GP = '.3gp',
  OGV = '.ogv',

  // Audio files
  MP3 = '.mp3',
  WAV = '.wav',
  OGG = '.ogg',
  AAC = '.aac',
  FLAC = '.flac',
  WMA = '.wma',
  M4A = '.m4a',
  OPUS = '.opus',

  // Markup and text
  MD = '.md',
  MARKDOWN = '.markdown',
  HTML = '.html',
  XML = '.xml',
  JSON = '.json',
  LOG = '.log',

  // Archives
  ZIP = '.zip',
  RAR = '.rar',
  SEVEN_Z = '.7z',
  TAR = '.tar',
  GZ = '.gz',

  // Special files
  WHITEBOARD = '.whiteboard',
  DRAWING = '.drawing',
  DRAW = '.draw',
  ODG = '.odg',
  ICO = '.ico'
}

/**
 * File type categories
 */
export enum FileTypeCategories {
  PDF = 'pdf',
  DOCUMENT = 'document',
  SPREADSHEET = 'spreadsheet',
  PRESENTATION = 'presentation',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  MARKDOWN = 'markdown',
  TEXT = 'text',
  CSV = 'csv',
  JSON = 'json',
  ARCHIVE = 'archive',
  WHITEBOARD = 'whiteboard',
  DRAWING = 'drawing',
  FILE = 'file'
}

/**
 * File type display names
 */
export enum FileTypeNames {
  PDF_DOCUMENT = 'PDF Document',
  WORD_DOCUMENT = 'Word Document',
  OPENDOCUMENT_TEXT = 'OpenDocument Text',
  TEXT_FILE = 'Text File',
  RICH_TEXT_FORMAT = 'Rich Text Format',

  EXCEL_SPREADSHEET = 'Excel Spreadsheet',
  OPENDOCUMENT_SPREADSHEET = 'OpenDocument Spreadsheet',
  CSV_FILE = 'CSV File',

  POWERPOINT_PRESENTATION = 'PowerPoint Presentation',
  OPENDOCUMENT_PRESENTATION = 'OpenDocument Presentation',

  PNG_IMAGE = 'PNG Image',
  JPEG_IMAGE = 'JPEG Image',
  GIF_IMAGE = 'GIF Image',
  BMP_IMAGE = 'BMP Image',
  WEBP_IMAGE = 'WebP Image',
  SVG_IMAGE = 'SVG Image',

  MP4_VIDEO = 'MP4 Video',
  AVI_VIDEO = 'AVI Video',
  QUICKTIME_VIDEO = 'QuickTime Video',
  WINDOWS_MEDIA_VIDEO = 'Windows Media Video',
  FLV_VIDEO = 'FLV Video',
  WEBM_VIDEO = 'WebM Video',
  MKV_VIDEO = 'MKV Video',

  MP3_AUDIO = 'MP3 Audio',
  WAV_AUDIO = 'WAV Audio',
  OGG_AUDIO = 'OGG Audio',
  AAC_AUDIO = 'AAC Audio',
  FLAC_AUDIO = 'FLAC Audio',
  WMA_AUDIO = 'WMA Audio',

  MARKDOWN = 'Markdown',
  JSON_FILE = 'JSON File',
  HTML_FILE = 'HTML File',
  XML_FILE = 'XML File',
  LOG_FILE = 'Log File',

  ZIP_ARCHIVE = 'ZIP Archive',
  RAR_ARCHIVE = 'RAR Archive',
  SEVEN_ZIP_ARCHIVE = '7-Zip Archive',
  TAR_ARCHIVE = 'TAR Archive',
  GZIP_ARCHIVE = 'GZip Archive',

  WHITEBOARD_DRAWING = 'Whiteboard Drawing',
  DRAWING_FILE = 'Drawing File',

  GENERIC_FILE = 'File'
}

/**
 * File icons (CoreUI icons)
 */
export enum FileIcons {
  FILE = 'cilFile',
  PDF = 'cilFile',
  DOCUMENT = 'cilDescription',
  SPREADSHEET = 'cilSpreadsheet',
  PRESENTATION = 'cilMonitor',
  PRESENTATION_PPT = 'cilMonitor',
  IMAGE = 'cilImage',
  VIDEO = 'cilVideo',
  AUDIO = 'cilMusicNote',
  MARKDOWN = 'cilCode',
  TEXT = 'cilNotes',
  TEXT_NOTES = 'cilNotes',
  CODE = 'cilCode',
  ARCHIVE = 'cilDataTransferDown',
  DRAWING = 'cilSitemap',
  WHITEBOARD = 'cilPenAlt',
  CSV = 'cilSpreadsheet',
}

/**
 * MIME type constants
 */
export enum MimeTypes {
  // Documents
  PDF = 'application/pdf',
  WORD = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  EXCEL = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  POWERPOINT = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  CSV = 'text/csv',

  // Images
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  WEBP = 'image/webp',
  SVG = 'image/svg+xml',

  // Videos
  MP4 = 'video/mp4',
  WEBM = 'video/webm',
  QUICKTIME = 'video/quicktime',

  // Audio
  MP3 = 'audio/mpeg',
  WAV = 'audio/wav',
  OGG = 'audio/ogg',

  // Text
  PLAIN_TEXT = 'text/plain',
  MARKDOWN = 'text/markdown',
  HTML = 'text/html',
  CSS = 'text/css',
  JAVASCRIPT = 'text/javascript',
  JSON = 'application/json',
  XML = 'application/xml',

  // Archives
  ZIP = 'application/zip',
  RAR = 'application/vnd.rar'
}

/**
 * Console log messages and emojis
 */
export enum LogMessages {
  // Success messages
  SUCCESS_FILE_CREATED = '✅ File created successfully',
  SUCCESS_THUMBNAIL_GENERATED = '✅ Thumbnail generated successfully',
  SUCCESS_UPLOAD_COMPLETE = '✅ Upload completed successfully',
  SUCCESS_PDF_LOADED = '✅ PDF loaded successfully',
  SUCCESS_VIDEO_THUMBNAIL = '✅ Video thumbnail generated successfully',
  SUCCESS_AUDIO_THUMBNAIL = '✅ Audio thumbnail generated successfully',
  SUCCESS_FALLBACK_THUMBNAIL = '✅ Fallback thumbnail created successfully',

  // Warning messages
  WARNING_NO_CONTENT = '⚠️ No content provided for thumbnail generation',
  WARNING_THUMBNAIL_FAILED = '⚠️ Thumbnail generation failed',
  WARNING_NO_ACCESS_URL = '⚠️ No access URL available',
  WARNING_UNSUPPORTED_FILE = '⚠️ Unsupported file type for thumbnail generation',
  WARNING_TIMEOUT = '⏱️ Video thumbnail generation timed out, trying fallback',

  // Error messages
  ERROR_THUMBNAIL_GENERATION = '❌ Error generating thumbnail',
  ERROR_FILE_CREATION = '❌ Failed to create file',
  ERROR_PDF_THUMBNAIL = '❌ Error generating PDF thumbnail',
  ERROR_VIDEO_THUMBNAIL = '❌ Error generating video thumbnail',
  ERROR_AUDIO_THUMBNAIL = '❌ Error generating audio thumbnail',
  ERROR_IMAGE_THUMBNAIL = '❌ Error generating image thumbnail',
  ERROR_MARKDOWN_THUMBNAIL = '❌ Error generating markdown thumbnail',
  ERROR_UPLOAD_FAILED = '❌ Upload failed',
  ERROR_INVALID_FILE = '❌ Invalid file provided',

  // Process messages
  PROCESS_GENERATING_PDF = '🖼️ Generating PDF thumbnail...',
  PROCESS_GENERATING_VIDEO = '🎥 Generating video thumbnail for file',
  PROCESS_GENERATING_AUDIO = '🎵 Generating audio thumbnail for file',
  PROCESS_GENERATING_IMAGE = '🖼️ Generating image thumbnail...',
  PROCESS_GENERATING_MARKDOWN = '📝 Generating markdown thumbnail...',
  PROCESS_CREATING_DOCUMENT = '📄 Creating new document',
  PROCESS_CREATING_SPREADSHEET = '📊 Creating new spreadsheet',
  PROCESS_CREATING_PRESENTATION = '📊 Creating new presentation',
  PROCESS_CREATING_MARKDOWN = '📝 Creating new markdown file',
  PROCESS_CREATING_PDF = '📄 Creating new PDF',
  PROCESS_PDF_LOADING = '🔄 Starting PDF thumbnail generation',
  PROCESS_VIDEO_LOADING = '📹 Video started loading for thumbnail generation',
  PROCESS_FALLBACK = '🎨 Creating fallback thumbnail',
  PROCESS_UPLOADING = '📤 Uploading file(s)...',
  PROCESS_CREATING_CSV = "PROCESS_CREATING_CSV"
}

/**
 * Default content templates
 */
export enum DefaultContent {
  MARKDOWN_TEMPLATE = `# New Document

Start writing your markdown content here...

## Getting Started

- Use **bold** and *italic* text
- Create lists and headers
- Add links and images

Click to begin editing your document!`,

  MARKDOWN_MINIMAL = `# New Document

Start writing your content here...`,

  PDF_PLACEHOLDER = 'New PDF Document',
  DOCUMENT_PLACEHOLDER = 'New Document',
  SPREADSHEET_PLACEHOLDER = 'New Spreadsheet',
  PRESENTATION_PLACEHOLDER = 'New Presentation'
}

/**
 * File extension arrays for easy checking
 */
export class FileExtensionGroups {
  static readonly DOCUMENTS = [
    FileExtensions.PDF,
    FileExtensions.DOC,
    FileExtensions.DOCX,
    FileExtensions.ODT,
    FileExtensions.TXT,
    FileExtensions.RTF
  ];

  static readonly SPREADSHEETS = [
    FileExtensions.XLS,
    FileExtensions.XLSX,
    FileExtensions.ODS,
    FileExtensions.CSV
  ];

  static readonly PRESENTATIONS = [
    FileExtensions.PPT,
    FileExtensions.PPTX,
    FileExtensions.ODP
  ];

  static readonly IMAGES = [
    FileExtensions.JPG,
    FileExtensions.JPEG,
    FileExtensions.PNG,
    FileExtensions.GIF,
    FileExtensions.BMP,
    FileExtensions.WEBP,
    FileExtensions.SVG,
    FileExtensions.ICO
  ];

  static readonly VIDEOS = [
    FileExtensions.MP4,
    FileExtensions.AVI,
    FileExtensions.MOV,
    FileExtensions.WMV,
    FileExtensions.FLV,
    FileExtensions.WEBM,
    FileExtensions.MKV,
    FileExtensions.M4V,
    FileExtensions.THREE_GP,
    FileExtensions.OGV
  ];

  static readonly AUDIO = [
    FileExtensions.MP3,
    FileExtensions.WAV,
    FileExtensions.OGG,
    FileExtensions.AAC,
    FileExtensions.FLAC,
    FileExtensions.WMA,
    FileExtensions.M4A,
    FileExtensions.OPUS
  ];

  static readonly MARKDOWN = [
    FileExtensions.MD,
    FileExtensions.MARKDOWN
  ];

  static readonly TEXT = [
    FileExtensions.TXT,
    FileExtensions.JSON,
    FileExtensions.XML,
    FileExtensions.HTML,
    FileExtensions.LOG
  ];

  static readonly ARCHIVES = [
    FileExtensions.ZIP,
    FileExtensions.RAR,
    FileExtensions.SEVEN_Z,
    FileExtensions.TAR,
    FileExtensions.GZ
  ];

  static readonly EXCEL_FILES = [
    FileExtensions.XLS,
    FileExtensions.XLSX,
    FileExtensions.CSV
  ];

  static readonly COLLABORA_SUPPORTED = [
    // FileExtensions.DOC,
    // FileExtensions.DOCX,
    // FileExtensions.ODT,
    // FileExtensions.XLS,
    // FileExtensions.XLSX,
    // FileExtensions.ODS,
    // FileExtensions.PPT,
    // FileExtensions.PPTX,
    // FileExtensions.ODP,
    // FileExtensions.CSV,
    // FileExtensions.ODG
    BizEntityTypeCategory.HayagrivaFileDrawing,
    BizEntityTypeCategory.HayagrivaFileDoc,
    BizEntityTypeCategory.HayagrivaFileSpreadsheet,
    BizEntityTypeCategory.HayagrivaFileCSV,
    BizEntityTypeCategory.HayagrivaFilePresentation,
    // BizEntityTypeCategory.HayagrivaFileWhiteboard
  ];
}

/**
 * Utility class for file type checking
 */
export class FileTypeUtils {
  /**
   * Check if file extension belongs to a specific group
   */
  static isFileType(extension: string, group: string[]): boolean {
    return group.includes(extension.toLowerCase() as FileExtensions);
  }

  /**
   * Get file type category from extension
   */
  static getFileTypeCategory(extension: string): FileTypeCategories {
    const ext = extension.toLowerCase();

    if (ext === FileExtensions.PDF) return FileTypeCategories.PDF;
    if (FileExtensionGroups.IMAGES.includes(ext as FileExtensions)) return FileTypeCategories.IMAGE;
    if (FileExtensionGroups.VIDEOS.includes(ext as FileExtensions)) return FileTypeCategories.VIDEO;
    if (FileExtensionGroups.AUDIO.includes(ext as FileExtensions)) return FileTypeCategories.AUDIO;
    if (FileExtensionGroups.MARKDOWN.includes(ext as FileExtensions)) return FileTypeCategories.MARKDOWN;
    if (FileExtensionGroups.SPREADSHEETS.includes(ext as FileExtensions)) return FileTypeCategories.SPREADSHEET;
    if (FileExtensionGroups.PRESENTATIONS.includes(ext as FileExtensions)) return FileTypeCategories.PRESENTATION;
    if (FileExtensionGroups.DOCUMENTS.includes(ext as FileExtensions)) return FileTypeCategories.DOCUMENT;
    if (FileExtensionGroups.TEXT.includes(ext as FileExtensions)) return FileTypeCategories.TEXT;
    if (FileExtensionGroups.ARCHIVES.includes(ext as FileExtensions)) return FileTypeCategories.ARCHIVE;
    if (ext === FileExtensions.WHITEBOARD) return FileTypeCategories.WHITEBOARD;
    if (ext === FileExtensions.DRAWING || ext === FileExtensions.DRAW || ext === FileExtensions.ODG) return FileTypeCategories.DRAWING;

    return FileTypeCategories.FILE;
  }

  /**
   * Get display name for file extension
   */
  static getFileTypeName(extension: string): string {
    const ext = extension.toLowerCase();

    const nameMap: Record<string, FileTypeNames> = {
      [FileExtensions.PDF]: FileTypeNames.PDF_DOCUMENT,
      [FileExtensions.DOC]: FileTypeNames.WORD_DOCUMENT,
      [FileExtensions.DOCX]: FileTypeNames.WORD_DOCUMENT,
      [FileExtensions.ODT]: FileTypeNames.OPENDOCUMENT_TEXT,
      [FileExtensions.TXT]: FileTypeNames.TEXT_FILE,
      [FileExtensions.RTF]: FileTypeNames.RICH_TEXT_FORMAT,
      [FileExtensions.XLS]: FileTypeNames.EXCEL_SPREADSHEET,
      [FileExtensions.XLSX]: FileTypeNames.EXCEL_SPREADSHEET,
      [FileExtensions.ODS]: FileTypeNames.OPENDOCUMENT_SPREADSHEET,
      [FileExtensions.CSV]: FileTypeNames.CSV_FILE,
      [FileExtensions.PPT]: FileTypeNames.POWERPOINT_PRESENTATION,
      [FileExtensions.PPTX]: FileTypeNames.POWERPOINT_PRESENTATION,
      [FileExtensions.ODP]: FileTypeNames.OPENDOCUMENT_PRESENTATION,
      [FileExtensions.PNG]: FileTypeNames.PNG_IMAGE,
      [FileExtensions.JPG]: FileTypeNames.JPEG_IMAGE,
      [FileExtensions.JPEG]: FileTypeNames.JPEG_IMAGE,
      [FileExtensions.GIF]: FileTypeNames.GIF_IMAGE,
      [FileExtensions.BMP]: FileTypeNames.BMP_IMAGE,
      [FileExtensions.WEBP]: FileTypeNames.WEBP_IMAGE,
      [FileExtensions.SVG]: FileTypeNames.SVG_IMAGE,
      [FileExtensions.MP4]: FileTypeNames.MP4_VIDEO,
      [FileExtensions.AVI]: FileTypeNames.AVI_VIDEO,
      [FileExtensions.MOV]: FileTypeNames.QUICKTIME_VIDEO,
      [FileExtensions.WMV]: FileTypeNames.WINDOWS_MEDIA_VIDEO,
      [FileExtensions.FLV]: FileTypeNames.FLV_VIDEO,
      [FileExtensions.WEBM]: FileTypeNames.WEBM_VIDEO,
      [FileExtensions.MKV]: FileTypeNames.MKV_VIDEO,
      [FileExtensions.MP3]: FileTypeNames.MP3_AUDIO,
      [FileExtensions.WAV]: FileTypeNames.WAV_AUDIO,
      [FileExtensions.OGG]: FileTypeNames.OGG_AUDIO,
      [FileExtensions.AAC]: FileTypeNames.AAC_AUDIO,
      [FileExtensions.FLAC]: FileTypeNames.FLAC_AUDIO,
      [FileExtensions.WMA]: FileTypeNames.WMA_AUDIO,
      [FileExtensions.MD]: FileTypeNames.MARKDOWN,
      [FileExtensions.MARKDOWN]: FileTypeNames.MARKDOWN,
      [FileExtensions.JSON]: FileTypeNames.JSON_FILE,
      [FileExtensions.HTML]: FileTypeNames.HTML_FILE,
      [FileExtensions.XML]: FileTypeNames.XML_FILE,
      [FileExtensions.LOG]: FileTypeNames.LOG_FILE,
      [FileExtensions.ZIP]: FileTypeNames.ZIP_ARCHIVE,
      [FileExtensions.RAR]: FileTypeNames.RAR_ARCHIVE,
      [FileExtensions.SEVEN_Z]: FileTypeNames.SEVEN_ZIP_ARCHIVE,
      [FileExtensions.TAR]: FileTypeNames.TAR_ARCHIVE,
      [FileExtensions.GZ]: FileTypeNames.GZIP_ARCHIVE,
      [FileExtensions.WHITEBOARD]: FileTypeNames.WHITEBOARD_DRAWING,
      [FileExtensions.DRAWING]: FileTypeNames.DRAWING_FILE,
      [FileExtensions.DRAW]: FileTypeNames.DRAWING_FILE
    };

    return nameMap[ext] || FileTypeNames.GENERIC_FILE;
  }

  /**
   * Get icon for file extension
   */
  static getFileIcon(extension: string): string {
    const category = this.getFileTypeCategory(extension);

    const iconMap: Record<FileTypeCategories, FileIcons> = {
      [FileTypeCategories.PDF]: FileIcons.PDF,
      [FileTypeCategories.DOCUMENT]: FileIcons.DOCUMENT,
      [FileTypeCategories.SPREADSHEET]: FileIcons.SPREADSHEET,
      [FileTypeCategories.PRESENTATION]: FileIcons.PRESENTATION,
      [FileTypeCategories.IMAGE]: FileIcons.IMAGE,
      [FileTypeCategories.VIDEO]: FileIcons.VIDEO,
      [FileTypeCategories.AUDIO]: FileIcons.AUDIO,
      [FileTypeCategories.MARKDOWN]: FileIcons.DOCUMENT,
      [FileTypeCategories.TEXT]: FileIcons.TEXT,
      [FileTypeCategories.JSON]: FileIcons.CODE,
      [FileTypeCategories.ARCHIVE]: FileIcons.ARCHIVE,
      [FileTypeCategories.WHITEBOARD]: FileIcons.DRAWING,
      [FileTypeCategories.DRAWING]: FileIcons.DRAWING,
      [FileTypeCategories.CSV]: FileIcons.CSV,
      [FileTypeCategories.FILE]: FileIcons.FILE
    };

    return iconMap[category] || FileIcons.FILE;
  }
}


/**
 * Utility class for file category validation
 * This class provides category-based checks instead of extension-based checks
 */
export class FileCategoryUtils {
  /**
   * Check if a category matches a specific BizEntityTypeCategory
   */
  static isCategoryOf(category: BizEntityTypeCategory, targetCategory: BizEntityTypeCategory): boolean {
    return category === targetCategory;
  }

  /**
   * Check if category is a PDF file
   */
  static isPdfCategory(category: BizEntityTypeCategory): boolean {
    return category === BizEntityTypeCategory.HayagrivaFilePDF;
  }

  /**
   * Check if category is an image file
   */
  static isImageCategory(category: BizEntityTypeCategory): boolean {
    return category === BizEntityTypeCategory.HayagrivaFileImage;
  }

  /**
   * Check if category is a video file
   */
  static isVideoCategory(category: BizEntityTypeCategory): boolean {
    return category === BizEntityTypeCategory.HayagrivaFileVideo;
  }

  /**
   * Check if category is a markdown file
   */
  static isMarkdownCategory(category: BizEntityTypeCategory): boolean {
    return category === BizEntityTypeCategory.HayagrivaFileMarkdown;
  }

  /**
   * Check if category is a document file
   */
  static isDocumentCategory(category: BizEntityTypeCategory): boolean {
    return category === BizEntityTypeCategory.HayagrivaFileDoc;
  }

  /**
   * Check if category is a spreadsheet file
   */
  static isSpreadsheetCategory(category: BizEntityTypeCategory): boolean {
    return category === BizEntityTypeCategory.HayagrivaFileSpreadsheet ||
      category === BizEntityTypeCategory.HayagrivaFileCSV;
  }

  /**
   * Check if category is a presentation file
   */
  static isPresentationCategory(category: BizEntityTypeCategory): boolean {
    return category === BizEntityTypeCategory.HayagrivaFilePresentation;
  }

  /**
   * Check if category is a drawing/diagram file
   */
  static isDrawingCategory(category: BizEntityTypeCategory): boolean {
    return category === BizEntityTypeCategory.HayagrivaFileDrawing;
  }

  /**
   * Check if category is a Collabora-supported file
   */
  static isCollaboraCategory(category: BizEntityTypeCategory): boolean {
    return category === BizEntityTypeCategory.HayagrivaFileDoc ||
      category === BizEntityTypeCategory.HayagrivaFileSpreadsheet ||
      category === BizEntityTypeCategory.HayagrivaFileCSV ||
      category === BizEntityTypeCategory.HayagrivaFilePresentation ||
      category === BizEntityTypeCategory.HayagrivaFileDrawing;
  }

  /**
   * Get BizEntityTypeCategory from filename extension
   */
  static getCategoryFromFilename(filename: string): BizEntityTypeCategory | null {
    if (!filename) return null;

    const extension = ('.' + (filename.toLowerCase().split('.').pop() || '')) as FileExtensions;
    const fileTypeCategory = FileTypeUtils.getFileTypeCategory(extension);

    // Map FileTypeCategories to BizEntityTypeCategory
    const categoryMap: Record<string, BizEntityTypeCategory> = {
      [FileTypeCategories.PDF]: BizEntityTypeCategory.HayagrivaFilePDF,
      [FileTypeCategories.IMAGE]: BizEntityTypeCategory.HayagrivaFileImage,
      [FileTypeCategories.VIDEO]: BizEntityTypeCategory.HayagrivaFileVideo,
      [FileTypeCategories.MARKDOWN]: BizEntityTypeCategory.HayagrivaFileMarkdown,
      [FileTypeCategories.DOCUMENT]: BizEntityTypeCategory.HayagrivaFileDoc,
      [FileTypeCategories.SPREADSHEET]: BizEntityTypeCategory.HayagrivaFileSpreadsheet,
      [FileTypeCategories.PRESENTATION]: BizEntityTypeCategory.HayagrivaFilePresentation,
      [FileTypeCategories.DRAWING]: BizEntityTypeCategory.HayagrivaFileDrawing,
    };

    return categoryMap[fileTypeCategory] || null;
  }

  /**
   * Check if a file object or filename matches a target category
   */
  static fileMatchesCategory(file: File, targetCategory: BizEntityTypeCategory): boolean {
    const fileCategory = this.getCategoryFromFilename(file.name);
    return fileCategory === targetCategory;
  }

  /**
   * Check if a filename extension matches any of the provided categories
   */
  static filenameMatchesCategories(filename: string, categories: BizEntityTypeCategory[]): boolean {
    const fileCategory = this.getCategoryFromFilename(filename);
    return fileCategory ? categories.includes(fileCategory) : false;
  }
}


/**
 * UI Action IDs for buttons and interactive elements
 */
export enum UIActions {
  // File operations
  UPLOAD_FILE = 'upload-file',
  CREATE_FOLDER = 'create-folder',
  IMPORT_URL = 'import-url',

  // Context menu actions
  VIEW = 'view',
  EDIT = 'edit',
  DELETE = 'delete',
  RENAME = 'rename',
  DOWNLOAD = 'download',
  DUPLICATE = 'duplicate',

  // File type creation actions
  CREATE_DOCUMENT = 'create-document',
  CREATE_SPREADSHEET = 'create-spreadsheet',
  CREATE_PRESENTATION = 'create-presentation',
  CREATE_MARKDOWN = 'create-markdown',
  CREATE_DIAGRAM = 'create-diagram',
  CREATE_PDF = 'create-pdf',
  CREATE_LIBRARY = 'create-library',

  // Explorer actions
  ITEM_CLICKED = 'itemClicked',
  SELECT_ITEM = 'selectItem',
  DRILL_DOWN = 'drillDown',

  // Navigation actions
  NEXT = 'next',
  PREVIOUS = 'previous',
  HOME = 'home',
  BACK = 'back',
  FORWARD = 'forward'
}

/**
 * UI Labels for buttons, menus, and interface elements
 */
export enum UILabels {
  // Button labels
  UPLOAD = 'Upload',
  UPLOAD_FILE = 'Upload File',
  CREATE = 'Create',
  CREATE_FOLDER = 'Create Folder',
  CREATE_LIBRARY = 'Create Library',
  IMPORT_FROM_URL = 'Import from URL',
  SAVE = 'Save',
  CANCEL = 'Cancel',
  OK = 'OK',
  CONFIRM = 'Confirm',
  CLOSE = 'Close',
  SUBMIT = 'Submit',

  // File type labels
  NEW_DOCUMENT = 'New Document',
  NEW_SPREADSHEET = 'New Spreadsheet',
  NEW_PRESENTATION = 'New Presentation',
  NEW_MARKDOWN = 'New Markdown',
  NEW_DIAGRAM = 'New Diagram',
  NEW_PDF = 'New PDF',
  NEW_FOLDER = 'New Folder',
  NEW_LIBRARY = 'New Library',

  // Menu labels
  ADD_TAGS = 'Add Tags',
  PROPERTIES = 'Properties',
  SAVE_AS_TEMPLATE = 'Save as template',
  SHARE = 'Share',
  PIN_TO_DASHBOARD = 'Pin To Dashboard',
  MOVE_TO_DASHBOARD = 'Move To Dashboard',
  DISCUSSION = 'Discussion',
  SIGN = 'Sign',

  // Context menu labels
  VIEW = 'View',
  EDIT = 'Edit',
  DELETE = 'Delete',
  RENAME = 'Rename',
  DOWNLOAD = 'Download',
  DUPLICATE = 'Duplicate',
  COPY = 'Copy',
  PASTE = 'Paste',
  CUT = 'Cut',
  BACK = 'Back',

  // Generic labels
  NAME = 'Name',
  TYPE = 'Type',
  TAG = 'Tag',
  TEMPLATE = 'Template',
  SIZE = 'Size',
  MODIFIED = 'Modified',
  OWNER = 'Owner',
  CREATED = 'Created',
  DESCRIPTION = 'Description',
  TAGS = 'Tags',

  // Action labels
  NEW = 'New',
  NEW_FROM_TEMPLATE = 'New From Template',
  LINK = 'Link'
}

/**
 * Menu constants
 */
export enum MenuConstants {
  SEPARATOR = 'separator'
}

/**
 * User interface messages and notifications
 */
export enum UIMessages {
  // Success messages
  FILE_UPLOADED_SUCCESS = 'File uploaded successfully',
  FILE_CREATED_SUCCESS = 'File created successfully',
  FOLDER_CREATED_SUCCESS = 'Folder created successfully',
  FILE_DELETED_SUCCESS = 'File deleted successfully',
  FILE_RENAMED_SUCCESS = 'File renamed successfully',

  // Error messages
  FILE_UPLOAD_ERROR = 'Error uploading file',
  FILE_CREATION_ERROR = 'Error creating file',
  FOLDER_CREATION_ERROR = 'Error creating folder',
  FILE_DELETION_ERROR = 'Error deleting file',
  FILE_RENAME_ERROR = 'Error renaming file',
  NO_FILE_SELECTED = 'No file selected',
  INVALID_FILE_TYPE = 'Invalid file type',
  FILE_TOO_LARGE = 'File is too large',
  STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD = 'Please select a Structural entity before uploading the document.',

  // Warning messages
  UNSAVED_CHANGES = 'You have unsaved changes',
  CONFIRM_DELETE = 'Are you sure you want to delete this item?',
  CONFIRM_OVERWRITE = 'This will overwrite the existing file. Continue?',
  NO_READ_ACCESS = 'You do not have read access to the newly created file. Please check your permissions.',

  // Info messages
  PROCESSING = 'Processing...',
  LOADING = 'Loading...',
  UPLOADING = 'Uploading...',
  SAVING = 'Saving...',
  GENERATING_THUMBNAIL = 'Generating thumbnail...',

  // Validation messages
  REQUIRED_FIELD = 'This field is required',
  INVALID_NAME = 'Invalid name',
  NAME_TOO_LONG = 'Name is too long',
  NAME_TOO_SHORT = 'Name is too short'
}

/**
 * CSS class names for styling
 */
export enum CSSClasses {
  // Layout classes
  CONTAINER = 'container',
  ROW = 'row',
  COLUMN = 'column',
  FLEX = 'd-flex',
  JUSTIFY_CENTER = 'justify-content-center',
  ALIGN_CENTER = 'align-items-center',

  // Button classes
  BTN = 'btn',
  BTN_PRIMARY = 'btn-primary',
  BTN_SECONDARY = 'btn-secondary',
  BTN_SUCCESS = 'btn-success',
  BTN_DANGER = 'btn-danger',
  BTN_WARNING = 'btn-warning',
  BTN_INFO = 'btn-info',
  BTN_LINK = 'btn-link',
  BTN_OUTLINE = 'btn-outline',

  // Form classes
  FORM_CONTROL = 'form-control',
  FORM_GROUP = 'form-group',
  FORM_LABEL = 'form-label',
  INPUT_GROUP = 'input-group',

  // Status classes
  ACTIVE = 'active',
  DISABLED = 'disabled',
  HIDDEN = 'hidden',
  VISIBLE = 'visible',
  LOADING = 'loading',
  ERROR = 'error',
  SUCCESS = 'success',
  WARNING = 'warning'
}

/**
 * Connection status for real-time collaboration and WebSocket connections
 */
export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected'
}

/**
 * Dialog and modal configurations
 */
export enum DialogTypes {
  CONFIRMATION = 'confirmation',
  ALERT = 'alert',
  PROMPT = 'prompt',
  FILE_PICKER = 'file-picker',
  FOLDER_PICKER = 'folder-picker',
  PROPERTIES = 'properties',
  SETTINGS = 'settings'
}

/**
 * Route and navigation constants
 */
export enum Routes {
  HOME = '/',
  DASHBOARD = '/dashboard',
  DOCUMENTS = '/documents',
  LIBRARIES = '/libraries',
  SETTINGS = '/settings',
  EMBED = 'embed',

  // Document type routes
  DOCUMENTS_PDF = '/documents/pdf',
  DOCUMENTS_DOC = '/documents/doc',
  DOCUMENTS_MARKDOWN = '/documents/markdown',
  DOCUMENTS_SPREADSHEET = '/documents/spreadsheet',
  DOCUMENTS_IMAGES = '/documents/images',
  DOCUMENTS_VIDEOS = '/documents/videos',
  DOCUMENTS_DIAGRAMS = '/documents/diagrams',
  DOCUMENTS_PRESENTATIONS = '/documents/presentation',
  DOCUMENTS_TEMPLATES = '/documents/templates'
}

/**
 * Query Parameter Names
 */
export enum QueryParams {
  ITEM = 'item',
  FILENAME = 'filename',
  PARENT_ID = 'parentId',
  BIZ_ENTITY_ID = 'bizEntityId',
  PARENT_BIZ_ENTITY_ID = 'parentBizEntityId'
}

/**
 * Storage keys for localStorage and sessionStorage
 */
export enum StorageKeys {
  USER_PREFERENCES = 'userPreferences',
  THEME = 'theme',
  LANGUAGE = 'language',
  RECENT_FILES = 'recentFiles',
  COLLABORA_BIZ_ENTITY_ID = 'collaboraBizEntityId',
  NAVIGATION_STATE = 'navigationState',
  UPLOAD_PROGRESS = 'uploadProgress',
  TEMP_DATA = 'tempData',
  USER_ID = 'userId',
  TENANT_ID = 'tenantId'
}

/**
 * Event types for component communication
 */
export enum EventTypes {
  FILE_UPLOADED = 'file-uploaded',
  FILE_CREATED = 'file-created',
  FILE_DELETED = 'file-deleted',
  FILE_RENAMED = 'file-renamed',
  FOLDER_CREATED = 'folder-created',
  NAVIGATION_CHANGED = 'navigation-changed',
  THEME_CHANGED = 'theme-changed',
  USER_LOGGED_IN = 'user-logged-in',
  USER_LOGGED_OUT = 'user-logged-out'
}

/**
 * API endpoint constants
 */
export enum APIEndpoints {
  BIZ_ENTITY = '/api/v0/biz-entity',
  BIZ_ENTITY_TYPE = '/api/v0/biz-entity-type',
  FILE_UPLOAD = '/api/v0/file',
  USER = '/api/v0/user',
  AUTHENTICATION = '/api/v0/auth',
  DOCUMENT = '/api/v0/document'
}

/**
 * Default values and configurations
 */
export enum DefaultValues {
  PAGE_SIZE = '20',
  TIMEOUT = '30000',
  MAX_FILE_SIZE = '10485760', // 10MB in bytes
  THUMBNAIL_WIDTH = '200',
  THUMBNAIL_HEIGHT = '200',
}

/**
 * URL Templates and External Services
 */
export enum URLTemplates {
  UI_AVATARS_API = 'https://ui-avatars.com/api/?name={name}&background={background}&color={color}',
  AVATAR_API_ENDPOINT = 'https://ui-avatars.com/api/?name=',
  DEFAULT_AVATAR = 'https://ui-avatars.com/api/?name=Optima+Stride&background=0D8ABC&color=fff',
  ADMIN_AVATAR = 'https://ui-avatars.com/api/?name=Admin+User&background=0D8ABC&color=fff',
  BENZAITEN_AVATAR = 'https://ui-avatars.com/api/?name=Benzaiten+Team&background=FF6B6B&color=fff',
  VAIC_AVATAR = 'https://ui-avatars.com/api/?name=Vaic+Team&background=4ECDC4&color=fff',
  APPS_AVATAR = 'https://ui-avatars.com/api/?name=Apps+Team&background=AA96DA&color=fff',
  JOHN_DOE_AVATAR = 'https://ui-avatars.com/api/?name=John+Doe&background=95E1D3&color=fff',
  CURRENT_USER_AVATAR = 'https://ui-avatars.com/api/?name=Current+User&background=0D8ABC&color=fff'
}

/**
 * File name templates and default names
 */
export enum FileNameTemplates {
  UNTITLED_DOCUMENT = 'Untitled-document.docx',
  UNTITLED_MARKDOWN = 'Untitled-markdown.md',
  UNTITLED_DIAGRAM = 'Untitled-diagram.docx',
  UNTITLED_PRESENTATION = 'Untitled-presentation.pptx',
  UNTITLED_SPREADSHEET = 'Untitled-spreadsheet.xlsx',
  UNTITLED_PDF = 'Untitled-document.pdf'
}

/**
 * Business Entity Constants
 */
export enum BizEntityConstants {
  ORGANIZATIONS_LIB_ID = 'lib-organizations',
  BIZ_ENTITY_ID_PARAM = 'bizEntityId',
  PARENT_BIZ_ENTITY_ID_PARAM = 'parentBizEntityId'
}

/**
 * Session Storage Keys for specific data types
 */
export enum SessionStorageKeys {
  COLLABORA_BIZ_ENTITY_ID = 'collaboraBizEntityId',
  VIDEO_BIZ_ENTITY_ID = 'videoBizEntityId',
  NAVIGATION_STATE = 'navigationState'
}

/**
 * Avatar Color Schemes
 */
export enum AvatarColors {
  PRIMARY_BACKGROUND = '0D8ABC',
  PRIMARY_COLOR = 'fff',
  BENZAITEN_BACKGROUND = 'FF6B6B',
  VAIC_BACKGROUND = '4ECDC4',
  APPS_BACKGROUND = 'AA96DA',
  JOHN_DOE_BACKGROUND = '95E1D3'
}

/**
 * API Path Segments
 */
export enum APIPathSegments {
  API_V0 = '/api/v0',
  API_HAYAGRIVA = '/api/hayagriva',
  BIZ_ENTITY = '/biz-entity',
  BIZ_ENTITY_TYPE = '/biz-entity-type',
  FILE = '/file',
  DOCUMENT = '/document',
  UPLOAD = '/upload',
  UPLOAD_MARKDOWN_ZIP = '/upload-markdown-zip',
  DOCUMENTS_UPLOAD = '/documents/upload'
}

/**
 * Error Messages and Log Identifiers
 */
export enum ErrorIdentifiers {
  IMAGE_LOAD_FAILED = 'Image load failed for bizEntityId:',
  VIDEO_LOAD_FAILED = 'Video load failed for bizEntityId:',
  THUMBNAIL_GENERATION_ERROR = 'Thumbnail generation failed for file:'
}

/**
 * Default Server URLs (for environments)
 */
export enum ServerURLs {
  DEV_BASE = 'https://dev.optimastride.com',
  PROD_BASE = 'https://optimastride.com',
  DEV_API = 'https://dev.optimastride.com/api/v0',
  PROD_API = 'https://optimastride.com/api/v0'
}

// Enum for context menu icons
export enum ContextMenuIcons {
  Download = 'pi pi-cloud-download',
  View = 'pi pi-eye',
  Share = 'pi pi-share-alt',
  Rename = 'pi pi-pencil',
  Delete = 'pi pi-trash',
  NewBook = 'pi pi-book',
  NewFolder = 'pi pi-folder',
  Plus = 'pi pi-fw pi-plus',
  Unpin = 'pi pi-minus-circle',
  Thumbtack = 'pi pi-thumbtack',
  MoveToDashboard = 'pi pi-arrows-h',
  Save = 'pi pi-save',
  InfoCircle = 'pi pi-info-circle',
  File = 'pi pi-file',
  Comments = 'pi pi-comments',
  Comment = 'pi pi-comment',
  SignIn = 'pi pi-sign-in',
  ArrowLeft = 'pi pi-arrow-left',
  Link = 'pi pi-link',
  Upload = 'pi pi-fw pi-upload',
  Envelope = 'pi pi-envelope',
  Edit = 'pi pi-pencil'
}
// Enum for context menu labels
export enum ContextMenuLabels {
  Download = 'Download',
  View = 'View',
  Share = 'Share',
  Rename = 'Rename',
  RevealInExplorer = 'Reveal in Explorer',
  Delete = 'Delete',
  New = 'New',
  Unpin_From_Dashboard = 'Unpin',
  RemovePin = 'Remove',
  Edit = 'Edit Properties',
}

/**
 * Keys used to identify columns in the Explorer card footer caption (layout view)
 */
export enum FooterCaptionKeys {
  FILE_ICON = 'file-icon',
  FILE_NAME = 'file-name',
  TAG = 'tag',
}

/**
 * Fallback display values used in the Explorer card footer caption
 */
export enum FooterCaptionDefaults {
  ICON = 'cilFile',
  NAME = '',
}